/**
 * What is claimed right now.
 *
 * The question this answers is "can I start work on this, or is somebody
 * already on it" — so the list leads with the file, then who, then how long
 * they have had it. A claim that has been held for days is usually one
 * somebody forgot to release, and showing the age plainly is what prompts
 * them to.
 */
import { useMemo } from 'react'
import { Lock as LockIcon, LockOpen, Hand, CheckCircle2 } from 'lucide-react'
import { locksHeld, type ProjectId, type Path, basename } from '@lattice/core'
import { useWorkspace, personOf, locksHeldNow } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { api } from '@/store/api'
import { FileIcon } from '@/components/FileIcon'
import { Avatar, Button, Tooltip, EmptyState, Badge } from '@/components/ui/primitives'
import { formatRelative, formatExact, pluralise } from '@/lib/format'

/** A claim older than this is worth drawing attention to. */
const STALE_AFTER_MS = 2 * 24 * 60 * 60 * 1000

export function LocksView({ projectId }: { projectId: ProjectId }) {
  const lockLog = useWorkspace(s => s.locks)
  const roster = useWorkspace(s => s.roster)
  const me = useWorkspace(s => s.user)
  const { go, open } = useUi()

  const held = useMemo(() => locksHeld(locksHeldNow(lockLog), projectId), [lockLog, projectId])
  const everywhere = useMemo(() => locksHeld(locksHeldNow(lockLog)), [lockLog])
  const elsewhere = everywhere.length - held.length

  if (held.length === 0) {
    return (
      <EmptyState
        icon={LockOpen}
        title="Nothing is locked in this project"
        hint={
          elsewhere > 0
            ? `${pluralise(elsewhere, 'file is', 'files are')} locked in other projects. Lock a file from its detail panel before you start editing it.`
            : 'Lock a file from its detail panel before you start editing it, so nobody else changes it underneath you.'
        }
      />
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <header className="sticky top-0 z-10 flex h-9 items-center gap-2 border-b border-[var(--border)] bg-app px-3">
        <LockIcon className="h-3.5 w-3.5 text-[var(--fg-subtle)]" strokeWidth={1.75} />
        <h2 className="text-sm font-medium">Locked files</h2>
        <span className="tabular text-xs text-[var(--fg-subtle)]">{held.length}</span>
      </header>

      <ul className="divide-y divide-[var(--border)]">
        {held.map(lock => {
          const holder = personOf(roster, lock.userId)
          const mine = lock.userId === me?.id
          const stale = Date.now() - Date.parse(lock.at) > STALE_AFTER_MS
          return (
            <li key={lock.id} className="flex items-start gap-2.5 px-3 py-2.5">
              <Avatar user={holder} size={22} className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <button
                  onClick={() => { go({ projectId, view: 'files' }); open(lock.path) }}
                  className="snap flex max-w-full items-center gap-1.5 text-left text-sm font-medium hover:text-[var(--accent)]"
                >
                  <FileIcon path={lock.path} />
                  <span className="truncate">{basename(lock.path as Path)}</span>
                </button>
                <p className="truncate text-[11px] text-[var(--fg-subtle)]">{lock.path}</p>
                <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
                  {mine ? 'You' : holder.name} locked this{' '}
                  <Tooltip label={formatExact(lock.at)}>
                    <span className="cursor-help underline decoration-dotted underline-offset-2">
                      {formatRelative(lock.at)}
                    </span>
                  </Tooltip>
                  {lock.note && <span className="text-[var(--fg-subtle)]"> — {lock.note}</span>}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                {stale && <Badge tone="modified">held {formatRelative(lock.at).replace(' ago', '')}</Badge>}
                {mine ? (
                  <Button size="sm" variant="secondary" onClick={() => void api.releaseLock(lock.id).catch(() => undefined)}>
                    <CheckCircle2 className="h-3 w-3" strokeWidth={1.75} /> Release
                  </Button>
                ) : (
                  <Tooltip label={`Ask ${holder.name.split(' ')[0]} to release it — writes a comment that mentions them`}>
                    <Button size="sm" variant="ghost" onClick={() => void api.requestUnlock(lock.id).catch(() => undefined)}>
                      <Hand className="h-3 w-3" strokeWidth={1.75} /> Request unlock
                    </Button>
                  </Tooltip>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
